import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { TextField, Paper, Grid, Button, Snackbar } from "@material-ui/core";
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SettingsBackupRestore as RestoreIcon,
} from "@material-ui/icons";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import moment from "moment";
import swal from "sweetalert";
import Alert from "@material-ui/lab/Alert";
import { connect } from "react-redux";
import {
  currentRouteSet,
  createdFloorSet,
  updatedFloorSet,
  disableRestoreFloorSet,
} from "../../actions/actions";
import { API_URL } from "../../config";
import {
  pathSpliter,
  checkAuthBranchWare,
  dateTimeFormat,
  sortRecordsNewestFirst,
} from "../../lib/functions";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
  },
  fullWidth: {
    width: "100%",
  },
}));

const ProductFloorsManage = ({
  location,
  currentRouteSet,
  authInfo,
  createdFloor,
  updatedFloor,
  floorDisableRestoreAction,
  createdFloorSet,
  updatedFloorSet,
  disableRestoreFloorSet,
}) => {
  const classes = useStyles();
  const [formValues, setFormValues] = useState({
    prod_floor_name: "",
    prod_floor_id: 0,
    action: "create",
  });
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [floors, setFloors] = useState([]);
  let [floorUpdateIndex, floorUpdateIndexSet] = useState("0");
  const floorRef = useRef(null)
  const [successMsg, setSuccessMsg] = useState({
    open: false,
    msg: "",
  });

  useEffect(() => {
    currentRouteSet(pathSpliter(location.pathname, 1));
    fetchFloors();
  }, [location.pathname, currentRouteSet]);

  useEffect(() => {
    if (createdFloor && checkAuthBranchWare(createdFloor.user_branch_id)) {
      setFloors((prevFloors) => [createdFloor.createdRow[0], ...prevFloors]);
      resetForm();
      showSuccess(createdFloor.msg);
    }
  }, [createdFloor]);

  useEffect(() => {
    if (updatedFloor && checkAuthBranchWare(updatedFloor.user_branch_id)) {
      setFloors((prevFloors) =>
        prevFloors.map((floor, index) =>
          index === updatedFloor.index ? updatedFloor.updatedRow[0] : floor
        )
      );
      resetForm();
      showSuccess(updatedFloor.msg);
    }
  }, [updatedFloor]);

  useEffect(() => {
    if (
      floorDisableRestoreAction &&
      checkAuthBranchWare(floorDisableRestoreAction.user_branch_id)
    ) {
      setFloors((prevFloors) =>
        prevFloors.map((floor, index) =>
          index === floorDisableRestoreAction.index
            ? floorDisableRestoreAction.disableRestoreRow[0]
            : floor
        )
      );
      showSuccess(floorDisableRestoreAction.msg);
    }
  }, [floorDisableRestoreAction]);

  const resetForm = () => {
    setFormValues({ prod_floor_name: "", prod_floor_id: 0, action: "create" });
  };

  const showSuccess = (msg) => {
    setSuccessMsg({ open: true, msg });
  };

  const handleClose = () => {
    setSuccessMsg({ open: false, msg: "" });
  };

  const handleFormInput = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const saveFormAction = async () => {
    if (formValues.prod_floor_name.trim() == "") {
      swal({
        title: "Product floor name is required",
        icon: "warning",
      });
    } else {
      formValues.floorUpdateIndex = floorUpdateIndex;
      setLoadingSave(true);
      await axios
        .post(`${API_URL}/api/floor-cu`, formValues, {
          headers: { "auth-token": authInfo.token },
        })
        .then(async (res) => {
          setLoadingSave(false);
          if (res?.data?.error) {
            swal({ title: res.data.message || "Failed to save floor", icon: "warning" });
            return false;
          }
          await fetchFloors();
        });
    }
    resetForm()
    if (floorRef.current) {
      floorRef.current.focus()
    }
  };

  const fetchFloors = async () => {
    setLoadingList(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/get-floors`, null, {
        headers: { "auth-token": authInfo.token },
      });
      setFloors(sortRecordsNewestFirst(data.message || []));
    } catch (error) {
      console.error("Error fetching floors:", error);
    } finally {
      setLoadingList(false);
    }
  };

  const handleAction = async (floorId, actionCond, index) => {
    if (actionCond === "disable") {
      const willContinue = await swal({
        title: "Are you sure?",
        text: "Do you want to delete this floor?",
        icon: "warning",
        buttons: ["No", "Yes"],
        dangerMode: true,
      });
      if (!willContinue) return;
    }

    try {
      const res = await axios.post(
        `${API_URL}/api/floor-disable-restore`,
        { prod_floor_id: floorId, action: actionCond, index },
        { headers: { "auth-token": authInfo.token } }
      );
      if (res?.data?.error) {
        swal({ title: res.data.message || "Failed to update floor", icon: "warning" });
        return false;
      }
      await fetchFloors();
    } catch (error) {
      console.error("Error disabling/restoring floor:", error);
    }
  };

  const columns = [
    { name: "prod_floor_id", options: { display: false } },
    { name: "prod_floor_name", label: "Floor Name", options: { sort: true } },
    {
      name: "prod_floor_created_isodt",
      label: "Created Date",
      options: {
        sort: true,
        customBodyRender: (value) => moment(value).format(dateTimeFormat),
      },
    },
    {
      name: "prod_floor_updated_isodt",
      label: "Updated Date",
      options: {
        sort: true,
        customBodyRender: (value) => moment(value).format(dateTimeFormat),
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        sort: false,
        customBodyRender: (_, { rowIndex }) => {
          const floor = floors[rowIndex];
          return (
            <div>
              <EditIcon
                onClick={() => {
                  floorUpdateIndexSet(rowIndex)
                  setFormValues({ ...floor, action: "update" })
                }}
              />
              {floor.prod_floor_status === "active" ? (
                <DeleteIcon
                  onClick={() =>
                    handleAction(floor.prod_floor_id, "disable", rowIndex)
                  }
                />
              ) : (
                <RestoreIcon
                  onClick={() =>
                    handleAction(floor.prod_floor_id, "restore", rowIndex)
                  }
                />
              )}
            </div>
          );
        },
      },
    },
  ];

  const options = {
    filterType: "checkbox",
    selectableRows: "none",
  };



  return (
    <div className={classes.root}>
      <Snackbar
        open={successMsg.open}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="success">
          {successMsg.msg}
        </Alert>
      </Snackbar>
      <Paper className={classes.paper}>
        <h2>Product Floor Entry</h2>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              inputRef={floorRef}
              label="Floor Name"
              name="prod_floor_name"
              variant="outlined"
              size="small"
              value={formValues.prod_floor_name}
              onChange={handleFormInput}
              className={classes.fullWidth}
              onKeyDown={(e) => {
                if (e.key == "Enter") {
                  saveFormAction()
                }
              }}
            />
          </Grid>
        </Grid>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          disabled={loadingSave}
          onClick={saveFormAction}
          className={classes.button}
        >
          Save
        </Button>
      </Paper>
      <Paper className={classes.paper}>
        {loadingList ? (
          <p>Loading...</p>
        ) : (
          <MUIDataTable
            title="Floors"
            data={floors}
            columns={columns}
            options={options}
          />
        )}
      </Paper>
    </div>
  );
};

const mapStateToProps = (state) => ({
  authInfo: state.authInfoReducer,
  createdFloor: state.createdFloorReducer,
  updatedFloor: state.updatedFloorReducer,
  floorDisableRestoreAction: state.disableRestoreFloorReducer,
});

export default connect(mapStateToProps, {
  currentRouteSet,
  createdFloorSet,
  updatedFloorSet,
  disableRestoreFloorSet,
})(ProductFloorsManage);
